import {
  ApiErrorBodySchema,
  buildPath,
  uuidv7,
  type EndpointDefinition,
  type EndpointRequest,
  type EndpointResponse,
} from '@webgame/shared';

/**
 * Failure of an API call. `code` is a stable server error code, or `network` when the server
 * could not be reached. UI code translates it with `translator.error(code)`.
 */
export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
    readonly requestId: string | null,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export const isApiError = (error: unknown): error is ApiError => error instanceof ApiError;

export interface ApiClientOptions {
  /** Base URL; empty for same-origin (the Vite proxy in development). */
  readonly baseUrl?: string;
  /** Returns the current bearer token, if any. */
  readonly getToken: () => string | null;
  /** Called on every 401 so the session layer can react (expired, replaced...). */
  readonly onUnauthorized?: (error: ApiError) => void;
  readonly fetch?: typeof fetch;
}

interface CallOptions {
  readonly signal?: AbortSignal;
}

/** A value with the entity tag it came with (null when it came with none). */
export interface Tagged<T> {
  readonly value: T;
  readonly etag: string | null;
}

/**
 * Typed HTTP client generated from the shared endpoint contracts. Responses are validated
 * against the contract so a server/client mismatch fails loudly at the boundary.
 */
export class ApiClient {
  readonly #fetch: typeof fetch;

  constructor(private readonly options: ApiClientOptions) {
    this.#fetch = options.fetch ?? globalThis.fetch.bind(globalThis);
  }

  async call<E extends EndpointDefinition>(
    endpoint: E,
    ...[request, options]: object extends EndpointRequest<E>
      ? [request?: EndpointRequest<E>, options?: CallOptions]
      : [request: EndpointRequest<E>, options?: CallOptions]
  ): Promise<EndpointResponse<E>> {
    const answer = await this.#send(endpoint, request, options, undefined);
    if (answer.value === undefined) throw new Error('Not modified without asking');
    return answer.value;
  }

  /**
   * Like `call`, revalidating what it got before (NET-CLIENT-CACHE C2): with its tag the server
   * answers `304` when nothing changed, nothing travels and its value comes back as it was.
   * `fresh` says whether it came down again.
   */
  async callTagged<E extends EndpointDefinition>(
    endpoint: E,
    request: object extends EndpointRequest<E>
      ? EndpointRequest<E> | undefined
      : EndpointRequest<E>,
    previous: Tagged<EndpointResponse<E>> | undefined,
  ): Promise<Tagged<EndpointResponse<E>> & { readonly fresh: boolean }> {
    const answer = await this.#send(endpoint, request, undefined, previous?.etag ?? undefined);
    if (answer.value === undefined) {
      if (!previous) throw new Error('Not modified without asking');
      return { ...previous, fresh: false };
    }
    return { value: answer.value, etag: answer.etag, fresh: true };
  }

  async #send<E extends EndpointDefinition>(
    endpoint: E,
    request: EndpointRequest<E> | undefined,
    options: CallOptions | undefined,
    ifNoneMatch: string | undefined,
  ): Promise<{ value: EndpointResponse<E> | undefined; etag: string | null }> {
    const req = (request ?? {}) as {
      params?: Record<string, string | number>;
      query?: Record<string, string | number | boolean>;
      body?: unknown;
    };
    const url = new URL(
      buildPath(endpoint.path, req.params),
      this.options.baseUrl ?? globalThis.location.origin,
    );
    for (const [key, value] of Object.entries(req.query ?? {})) {
      url.searchParams.set(key, String(value));
    }

    const requestId = uuidv7();
    const headers: Record<string, string> = { 'x-request-id': requestId };
    const token = this.options.getToken();
    // The dev tools take it too: a session raised to administrator reaches them (ADMIN-CONSOLE).
    if (token && endpoint.access !== 'none') {
      headers['authorization'] = `Bearer ${token}`;
    }
    if (req.body !== undefined) headers['content-type'] = 'application/json';
    // Named by the page, the browser's own cache stays out of it: a 304 reaches this code.
    if (ifNoneMatch !== undefined) headers['if-none-match'] = ifNoneMatch;

    let response: Response;
    try {
      response = await this.#fetch(url, {
        method: endpoint.method,
        headers,
        ...(req.body !== undefined && { body: JSON.stringify(req.body) }),
        ...(options?.signal && { signal: options.signal }),
      });
    } catch (cause) {
      if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
      throw new ApiError('network', 'Server unreachable', 0, requestId, cause);
    }
    const etag = response.headers.get('etag');
    if (response.status === 304 && ifNoneMatch !== undefined) return { value: undefined, etag };

    const payload: unknown = await response.json().catch(() => null);

    if (!response.ok) {
      const parsed = ApiErrorBodySchema.safeParse(payload);
      const error = parsed.success
        ? new ApiError(
            parsed.data.error.code,
            parsed.data.error.message,
            response.status,
            parsed.data.error.requestId,
            parsed.data.error.details,
          )
        : new ApiError('unknown', `HTTP ${response.status}`, response.status, requestId);
      if (response.status === 401) this.options.onUnauthorized?.(error);
      throw error;
    }

    const parsed = endpoint.response.safeParse(payload);
    if (!parsed.success) {
      throw new ApiError(
        'unknown',
        `Response of ${endpoint.method} ${endpoint.path} violates its contract`,
        response.status,
        requestId,
        parsed.error.issues,
      );
    }
    return { value: parsed.data as EndpointResponse<E>, etag };
  }
}
